import { formatCurrencyCompact } from '../parity-dev/cfo/ceo/ceoFormat'

/**
 * The `their_numbers` math: three inputs a prospect knows cold — annual
 * revenue, headcount, how long customers take to pay — through the same
 * arithmetic the product runs on the sample company. Their data, our engine,
 * zero generation cost. (And, deliberately, a qualification form wearing a
 * feature's clothes: the inputs are exactly what sales wants to know.)
 *
 * All pure: inputs in, formatted strings out. Nothing here fetches, renders,
 * or invents — a number either follows from the three inputs or it is not
 * shown.
 */
export interface TheirNumbersInput {
  annualRevenue: number
  headcount: number
  collectionDays: number
}

export interface TheirNumbersResult {
  revenuePerHead: string
  cashInReceivables: string
  fiveDaysFaster: string
  /** Positive when they collect slower than the sample company. */
  dsoGapDays: number | null
}

export function parseTheirNumbers(raw: { revenue: string; headcount: string; days: string }): TheirNumbersInput | null {
  const num = (s: string) => Number(String(s).replace(/[$,\s]/g, ''))
  const annualRevenue = num(raw.revenue)
  const headcount = num(raw.headcount)
  const collectionDays = num(raw.days)
  if (![annualRevenue, headcount, collectionDays].every((v) => Number.isFinite(v) && v > 0)) return null
  if (headcount > 100_000 || collectionDays > 365) return null
  return { annualRevenue, headcount, collectionDays }
}

export function computeTheirNumbers(input: TheirNumbersInput, sampleDsoDays: number | null): TheirNumbersResult {
  const daily = input.annualRevenue / 365
  return {
    revenuePerHead: formatCurrencyCompact(input.annualRevenue / input.headcount),
    cashInReceivables: formatCurrencyCompact(daily * input.collectionDays),
    fiveDaysFaster: formatCurrencyCompact(daily * 5),
    dsoGapDays:
      typeof sampleDsoDays === 'number' && Number.isFinite(sampleDsoDays)
        ? Math.round(input.collectionDays - sampleDsoDays)
        : null,
  }
}

/** "48 days" (the fact-map format) → 48; anything else → null. */
export function parseDsoFact(fact: string | undefined): number | null {
  const m = /^(\d+(?:\.\d+)?)\s*days?$/.exec(String(fact ?? '').trim())
  return m ? Number(m[1]) : null
}
